/**
 * Shared cell alignment utilities for PDF report rendering.
 * Used by both PDFPreviewModal and PDFReportRenderer to ensure
 * consistent alignment between preview and export.
 */

export type CellAlignment = 'center' | 'leftcenter' | 'topleft'

/**
 * Columns that should use LEFT-CENTER alignment (left horizontal, center vertical).
 * These typically contain text that can be multi-line but should be vertically centered.
 */
export const LEFT_CENTER_COLUMNS = new Set([
  'owner',
  'broker',
  'options'
])

/**
 * Columns that should ALWAYS use CENTER alignment (both horizontal and vertical),
 * regardless of content (even if multi-line).
 */
export const FORCE_CENTER_COLUMNS = new Set([
  'serial',      // S/N column - fully centered
  'year',        // MAN Date / EIS Date
  'dateListed',  // Date Listed
  'askingTake',  // Asking / Take
  'dates',       // Listed / Sold
  'prices',      // Asking / Sold
  'dateRemoved'  // Date Removed
])

/**
 * Determines the alignment for a table cell based on its column and content.
 *
 * ALIGNMENT RULES:
 * 1. If value is '-' (placeholder): always center (H+V)
 * 2. If column is in FORCE_CENTER_COLUMNS: always center (H+V)
 * 3. If column is in LEFT_CENTER_COLUMNS: always left-center (left H, center V)
 * 4. For other columns: center by default (short values, numbers, etc.)
 *
 * @param value - The cell content (string)
 * @param columnKey - The column data key (e.g., 'owner', 'aftt')
 * @returns 'center', 'leftcenter', or 'topleft'
 */
export function getCellAlignment(value: string, columnKey: string): CellAlignment {
  // Special case: '-' placeholder should always be centered
  const trimmed = (value || '').trim()
  if (trimmed === '-') {
    return 'center'
  }

  // Check if column should always be centered
  if (FORCE_CENTER_COLUMNS.has(columnKey)) {
    return 'center'
  }

  // Check if column should be left-center
  if (LEFT_CENTER_COLUMNS.has(columnKey)) {
    return 'leftcenter'
  }

  // Default to center for all other columns (AFTT, EMP, DMG, Rel. Value, custom columns, etc.)
  return 'center'
}

/**
 * Returns the CSS class name for the given alignment (PDF context).
 * These classes are defined in pdf-report-legacy.css and are scoped under .ij-report-root.
 *
 * @param alignment - 'center', 'leftcenter', or 'topleft'
 * @returns CSS class name for PDF rendering
 */
export function getAlignmentClass(alignment: CellAlignment): string {
  switch (alignment) {
    case 'center':
      return 'pdf-cell-center'
    case 'leftcenter':
      return 'pdf-cell-leftcenter'
    case 'topleft':
      return 'pdf-cell-topleft'
    default:
      return 'pdf-cell-center'
  }
}

/**
 * Returns the CSS class name for the given alignment (Live View context).
 * These classes are defined in index.css and work outside .ij-report-root scope.
 *
 * @param alignment - 'center', 'leftcenter', or 'topleft'
 * @returns CSS class name for Live View rendering
 */
export function getLiveViewAlignmentClass(alignment: CellAlignment): string {
  switch (alignment) {
    case 'center':
      return 'ij-lv-cell-center'
    case 'leftcenter':
      return 'ij-lv-cell-leftcenter'
    case 'topleft':
      return 'ij-lv-cell-topleft'
    default:
      return 'ij-lv-cell-center'
  }
}

/**
 * Returns the appropriate alignment class based on rendering context.
 *
 * @param alignment - 'center', 'leftcenter', or 'topleft'
 * @param isPdfContext - True for PDF rendering (uses scoped classes), false for Live View
 * @returns CSS class name
 */
export function getContextualAlignmentClass(alignment: CellAlignment, isPdfContext: boolean): string {
  return isPdfContext ? getAlignmentClass(alignment) : getLiveViewAlignmentClass(alignment)
}

